import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { SlidersHorizontal, LayoutGrid, List, X, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { ProductCard } from "../components/ProductCard";
import { SectionHeading } from "../components/Section";
import { getProducts, getFilters, formatEUR } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

const RES_LABEL = { "1080p": "Full HD 1080p", "720p": "HD 720p" };

export default function Shop() {
  const { t, lang } = useLang();
  const [sp, setSp] = useSearchParams();
  const { slug: catParam } = useParams();
  const [filters, setFilters] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [price, setPrice] = useState([50, 300]);

  const [f, setF] = useState({
    category: catParam || sp.get("category") || "all",
    search: sp.get("search") || "",
    resolution: "", throw_type: "", use_case: "", availability: "", min_lumens: 0,
    badge: "", portable: null, sort: "featured",
  });

  useEffect(() => { getFilters().then(setFilters).catch(() => {}); }, []);
  useEffect(() => { setF((p) => ({ ...p, category: catParam || "all" })); }, [catParam]);
  useEffect(() => { setF((p) => ({ ...p, search: sp.get("search") || "" })); }, [sp]);

  useEffect(() => {
    setLoading(true);
    const params = { lang, sort: f.sort, min_price: price[0], max_price: price[1] };
    if (f.category && f.category !== "all") params.category = f.category;
    if (f.search) params.search = f.search;
    if (f.resolution) params.resolution = f.resolution;
    if (f.throw_type) params.throw_type = f.throw_type;
    if (f.use_case) params.use_case = f.use_case;
    if (f.availability) params.availability = f.availability;
    if (f.min_lumens) params.min_lumens = f.min_lumens;
    if (f.badge) params.badge = f.badge;
    if (f.portable !== null) params.portable = f.portable;
    getProducts(params).then((d) => { setProducts(d.products); setLoading(false); }).catch(() => setLoading(false));
  }, [f, price, lang]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const clearAll = () => { setF({ category: "all", search: "", resolution: "", throw_type: "", use_case: "", availability: "", min_lumens: 0, badge: "", portable: null, sort: "featured" }); setPrice([50, 300]); setSp({}); };

  const FilterBody = () => (
    <Accordion type="multiple" defaultValue={["cat", "price", "use", "res"]} className="w-full">
      <AccordionItem value="cat">
        <AccordionTrigger className="text-sm">{t("nav.categories")}</AccordionTrigger>
        <AccordionContent>
          <div className="space-y-1">
            <FilterRadio label={t("common.viewAll")} active={f.category === "all"} onClick={() => set("category", "all")} />
            {filters?.categories.map((c) => (
              <FilterRadio key={c.slug} label={lang === "hr" ? c.name_hr : c.name_en} active={f.category === c.slug} onClick={() => set("category", c.slug)} testid={`filter-cat-${c.slug}`} />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="price">
        <AccordionTrigger className="text-sm">{t("common.price")}</AccordionTrigger>
        <AccordionContent>
          <Slider data-testid="filter-price-slider" min={50} max={300} step={10} value={price} onValueChange={setPrice} className="mt-3" />
          <div className="flex justify-between mt-2 font-mono text-xs text-muted-foreground"><span>{formatEUR(price[0])}</span><span>{formatEUR(price[1])}</span></div>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="use">
        <AccordionTrigger className="text-sm">{t("common.useCase")}</AccordionTrigger>
        <AccordionContent>
          <div className="space-y-1">
            {filters?.use_cases.map((u) => (
              <FilterCheck key={u} label={t(`useCases.${u}`)} checked={f.use_case === u} onClick={() => set("use_case", f.use_case === u ? "" : u)} />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="res">
        <AccordionTrigger className="text-sm">{t("common.resolution")}</AccordionTrigger>
        <AccordionContent>
          <div className="space-y-1">
            {filters?.resolutions.map((r) => <FilterCheck key={r} label={RES_LABEL[r] || r} checked={f.resolution === r} onClick={() => set("resolution", f.resolution === r ? "" : r)} />)}
          </div>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="throw">
        <AccordionTrigger className="text-sm">{t("common.throw")}</AccordionTrigger>
        <AccordionContent>
          <div className="space-y-1">
            {filters?.throw_types.map((r) => <FilterCheck key={r} label={t(`throwTypes.${r}`)} checked={f.throw_type === r} onClick={() => set("throw_type", f.throw_type === r ? "" : r)} />)}
            <FilterCheck label={t("common.portable")} checked={f.portable === true} onClick={() => set("portable", f.portable === true ? null : true)} />
          </div>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="badge">
        <AccordionTrigger className="text-sm">{t("specL.badges")}</AccordionTrigger>
        <AccordionContent>
          <div className="flex flex-wrap gap-1.5">
            {filters?.badges.map((b) => (
              <button key={b} onClick={() => set("badge", f.badge === b ? "" : b)}
                className={`chip ${f.badge === b ? "text-cyanx shadow-glow-cyan" : ""}`}>{b}</button>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  return (
    <div className="pt-24 pb-16 container-lumeo">
      <SectionHeading center={false} eyebrow={t("nav.shop")} title={t("nav.shop")} titleMuted={t("shopPage.titleMuted")} />
      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl card-ceramic p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("common.filters")}</span>
              <button onClick={clearAll} className="text-xs text-cyanx hover:underline">{t("common.clearAll")}</button>
            </div>
            <FilterBody />
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="secondary" className="lg:hidden hairline" data-testid="shop-filter-button"><SlidersHorizontal className="h-4 w-4 mr-2" />{t("common.filters")}</Button>
                </SheetTrigger>
                <SheetContent side="left" className="glass border-border w-[85vw] overflow-y-auto">
                  <SheetHeader><SheetTitle>{t("common.filters")}</SheetTitle></SheetHeader>
                  <div className="mt-4"><FilterBody /></div>
                  <Button onClick={clearAll} variant="ghost" className="w-full mt-4">{t("common.clearAll")}</Button>
                </SheetContent>
              </Sheet>
              <span className="text-sm text-muted-foreground">{loading ? t("common.loading") : `${products.length} ${t("common.results")}`}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center rounded-lg hairline p-0.5">
                <button onClick={() => setView("grid")} className={`h-8 w-8 rounded-md inline-flex items-center justify-center ${view === "grid" ? "bg-cyanx/15 text-cyanx" : "text-muted-foreground"}`}><LayoutGrid className="h-4 w-4" /></button>
                <button onClick={() => setView("list")} className={`h-8 w-8 rounded-md inline-flex items-center justify-center ${view === "list" ? "bg-cyanx/15 text-cyanx" : "text-muted-foreground"}`}><List className="h-4 w-4" /></button>
              </div>
              <Select value={f.sort} onValueChange={(v) => set("sort", v)}>
                <SelectTrigger data-testid="shop-sort-select" className="w-[170px] bg-[#0b0d12] border-border"><SelectValue placeholder={t("common.sortBy")} /></SelectTrigger>
                <SelectContent className="glass border-border">
                  {["featured", "price_asc", "price_desc", "lumens", "rating"].map((s) => (
                    <SelectItem key={s} value={s}>{t(`sort.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {f.search && (
            <div className="mb-4 inline-flex items-center gap-2 chip"><Search className="h-3 w-3" />{f.search}<button onClick={() => { set("search", ""); setSp({}); }}><X className="h-3 w-3" /></button></div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="rounded-2xl card-ceramic h-80 animate-pulse" />)}</div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">{t("common.noResults")}</div>
          ) : (
            <div className={view === "grid" ? "grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" : "grid grid-cols-1 gap-4"}>
              {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const FilterRadio = ({ label, active, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`w-full text-left px-2 py-1.5 rounded-md text-sm capitalize transition-colors ${active ? "text-cyanx bg-cyanx/10" : "text-foreground/75 hover:bg-white/5"}`}>{label}</button>
);
const FilterCheck = ({ label, checked, onClick }) => (
  <div onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm capitalize hover:bg-white/5 cursor-pointer">
    <Checkbox checked={checked} className="pointer-events-none data-[state=checked]:bg-cyanx data-[state=checked]:border-cyanx" />
    <span className={checked ? "text-cyanx" : "text-foreground/75"}>{label}</span>
  </div>
);
